const CAP20 = {
  id: 20,
  slug: "cap20-criptografia-clave-publica-1",
  title: "Capítulo 20 — Conceptos básicos de la criptografía de clave pública (I)",
  sections: [
    {
      heading: "Introducción a la criptografía",
      text: [
        "La criptografía es una disciplina que, mediante técnicas matemáticas y algoritmos, permite proteger datos e información, asegurando que solo las partes autorizadas puedan acceder a ellos.",
        "En la tecnología blockchain, la criptografía es esencial para garantizar la seguridad, integridad, autenticidad y privacidad de las transacciones.",
        "Sin criptografía, no existiría confianza ni protección frente a manipulaciones, fraudes o suplantaciones, lo que haría inviable el uso de tecnologías descentralizadas como blockchain."
      ]
    },
    {
      heading: "La criptografía en blockchain",
      text: [
        "La blockchain funciona como un libro mayor distribuido que registra información de forma segura e inmutable.",
        "Esto es posible gracias a técnicas criptográficas que permiten verificar transacciones, proteger identidades y asegurar que los datos no puedan ser alterados sin dejar rastro.",
        "Gracias a la criptografía, las redes blockchain permiten transacciones seguras entre usuarios, sin la necesidad de un intermediario de confianza."
      ]
    },
    {
      heading: "Cifrado y descifrado de datos",
      text: [
        "**Cifrado**: es el proceso de convertir información legible (texto plano) en un formato ilegible (texto cifrado), utilizando un algoritmo criptográfico. Esto impide que personas no autorizadas puedan leer la información.",
        "**Descifrado**: es el proceso inverso. Convierte el texto cifrado nuevamente en texto legible, usando la clave adecuada.",
        "En blockchain, este proceso permite proteger datos sensibles, comunicaciones y transacciones, garantizando su seguridad incluso en redes públicas y descentralizadas."
      ],
      list: [
        "🔐 Cifrado = convertir datos legibles en datos protegidos.",
        "🔑 Descifrado = volver los datos a su estado original, solo con la clave correcta."
      ]
    },
    {
      heading: "Tipos de criptografía",
      text: [
        "En el mundo moderno, existen dos tipos principales de criptografía: **simétrica** y **asimétrica**. Ambas siguen siendo usadas en sistemas digitales, pero tienen diferencias fundamentales."
      ]
    },
    {
      heading: "Criptografía simétrica (una sola clave)",
      text: [
        "La criptografía simétrica utiliza **una única clave** para cifrar y descifrar la información.",
        "Es un método antiguo, utilizado desde hace miles de años, y sigue siendo eficiente y rápido.",
        "Su principal problema es la seguridad en el intercambio de claves: si alguien intercepta la clave, puede descifrar toda la información."
      ],
      list: [
        "🔹 Usa una sola clave secreta.",
        "🔹 Rápida y eficiente para grandes cantidades de datos.",
        "🔹 Vulnerable si la clave se comparte o se filtra."
      ]
    },
    {
      heading: "Criptografía asimétrica o de clave pública",
      text: [
        "La criptografía asimétrica, también llamada **de clave pública**, fue desarrollada por Whitfield Diffie y Martin Hellman en los años 70.",
        "Utiliza **dos claves diferentes pero matemáticamente relacionadas**:",
        "🔑 **Clave pública**: se puede compartir y usar para cifrar información.",
        "🔐 **Clave privada**: permanece en secreto y se usa para descifrar la información.",
        "Este sistema permite enviar información cifrada sin necesidad de compartir previamente una clave secreta."
      ],
      list: [
        "🔹 Mayor seguridad en la comunicación.",
        "🔹 No requiere compartir la clave privada.",
        "🔹 Es la base de la tecnología blockchain, firmas digitales y wallets."
      ]
    },
    {
      heading: "Ejemplo sencillo de funcionamiento",
      text: [
        "Imagina que quieres enviar un mensaje seguro a otra persona:",
        "1️⃣ Tú usas la clave pública del destinatario para cifrar el mensaje.",
        "2️⃣ Solo el destinatario, con su **clave privada**, podrá descifrarlo.",
        "Así, incluso si alguien intercepta el mensaje durante el envío, **no podrá leerlo** porque no tiene la clave privada."
      ]
    },
    {
      heading: "Importancia en blockchain",
      text: [
        "La criptografía de clave pública es uno de los pilares de la tecnología blockchain.",
        "Gracias a ella:",
        "🔹 Las personas pueden realizar transacciones sin revelar su identidad real.",
        "🔹 Cada usuario puede firmar digitalmente sus operaciones.",
        "🔹 El sistema puede verificar transacciones sin necesidad de un intermediario.",
        "Este sistema logra que blockchain sea **segura, confiable y descentralizada**."
      ]
    },
    {
      heading: "¿Qué veremos en el siguiente capítulo?",
      text: [
        "En el próximo capítulo profundizaremos en:",
        "🔹 ¿Cómo se generan las claves públicas y privadas?",
        "🔹 ¿Qué es exactamente una firma digital?",
        "🔹 ¿Cómo se usan en wallets, Ethereum y Bitcoin?",
        "🔹 ¿Qué es ECDSA y por qué es el estándar en blockchain?"
      ]
    }
  ]
};

export default CAP20;
